import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import AdminNav from '../../components/admin/AdminNav';
import {
  listAdminCourses, createAdminCourse, updateAdminCourse, deleteAdminCourse,
} from '../../services/adminCourse.service';

const TYPE_BADGE = {
  macro: 'bg-reset-terracotta/10 text-reset-terracotta',
  micro: 'bg-reset-sage/10 text-reset-sage',
};

export default function AdminCourses() {
  const [courses, setCourses] = useState(null);
  const [error, setError]     = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      setCourses(await listAdminCourses());
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load courses.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePublish = async (course) => {
    await updateAdminCourse(course.id, { is_published: !course.is_published });
    load();
  };

  const remove = async (course) => {
    if (!window.confirm(`Delete "${course.title}"? This also deletes all of its lessons and can't be undone.`)) return;
    await deleteAdminCourse(course.id);
    load();
  };

  const macro = courses?.filter(c => c.type === 'macro') || [];
  const micro = courses?.filter(c => c.type === 'micro') || [];

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <AdminNav />

        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-serif text-2xl font-bold text-reset-brown">Course Management</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {courses ? `${courses.length} course${courses.length === 1 ? '' : 's'}` : '—'}
            </p>
          </div>
          <button
            onClick={() => setShowForm(v => !v)}
            className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors"
          >
            {showForm ? 'Cancel' : '+ New Course'}
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>
        )}

        {showForm && (
          <NewCourseForm
            onCreated={() => { setShowForm(false); load(); }}
            onError={(msg) => setError(msg)}
          />
        )}

        {!courses ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : (
          <>
            <CourseSection title="Macro Courses" courses={macro} onTogglePublish={togglePublish} onDelete={remove} />
            <CourseSection title="Micro Courses" courses={micro} onTogglePublish={togglePublish} onDelete={remove} />
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

// ─── New course form ────────────────────────────────────────────────────────

function NewCourseForm({ onCreated, onError }) {
  const [type, setType] = useState('macro');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [displayOrder, setDisplayOrder] = useState(1);
  const [categories, setCategories] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await createAdminCourse({
        type,
        title: title.trim(),
        description: description.trim() || undefined,
        displayOrder: Number(displayOrder) || 0,
        categories: categories.split(',').map(c => c.trim()).filter(Boolean),
      });
      onCreated();
    } catch (err) {
      onError(err.response?.data?.error || 'Could not create course.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm p-5 mb-6 space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
          <select
            value={type}
            onChange={e => setType(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          >
            <option value="macro">Macro (fixed sequence)</option>
            <option value="micro">Micro (free choice)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Display order</label>
          <input
            type="number"
            value={displayOrder}
            onChange={e => setDisplayOrder(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          required
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          rows={2}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
      </div>

      {type === 'micro' && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Categories (comma-separated)</label>
          <input
            type="text"
            value={categories}
            onChange={e => setCategories(e.target.value)}
            placeholder="Boundary Setting, Actually Doing Self-Care"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
        </div>
      )}

      <button
        type="submit"
        disabled={saving}
        className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
      >
        {saving ? 'Creating…' : 'Create course'}
      </button>
    </form>
  );
}

// ─── Course list section ────────────────────────────────────────────────────

function CourseSection({ title, courses, onTogglePublish, onDelete }) {
  if (courses.length === 0) return null;
  return (
    <section className="mb-8">
      <h2 className="text-base font-bold text-reset-brown mb-3">{title}</h2>
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-50">
        {courses.map(course => (
          <div key={course.id} className="flex items-center justify-between gap-3 px-5 py-3.5 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Link to={`/admin/courses/${course.id}`} className="text-sm font-semibold text-reset-brown hover:underline">
                  {course.title}
                </Link>
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${TYPE_BADGE[course.type]}`}>
                  {course.type}
                </span>
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                  course.is_published ? 'bg-green-100 text-green-700' : course.coming_soon ? 'bg-reset-gold/30 text-reset-brown' : 'bg-gray-100 text-gray-500'
                }`}>
                  {course.is_published ? 'Published' : course.coming_soon ? 'Coming soon' : 'Draft'}
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                {course.lesson_count} lesson{course.lesson_count === 1 ? '' : 's'}
                {course.categories?.length > 0 ? ` · ${course.categories.join(', ')}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => onTogglePublish(course)}
                className="text-xs font-medium text-gray-500 hover:text-reset-brown px-2 py-1"
              >
                {course.is_published ? 'Unpublish' : 'Publish'}
              </button>
              <Link
                to={`/admin/courses/${course.id}`}
                className="text-xs font-medium text-reset-terracotta hover:underline px-2 py-1"
              >
                Manage →
              </Link>
              <button
                onClick={() => onDelete(course)}
                className="text-xs font-medium text-red-400 hover:text-red-600 px-2 py-1"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
